import { SHOWROOM_PATH_IDENTIFIER } from "@faire/retailer-shared/categories/pathIdentifier";
import { AnimatedCategoryTile } from "@faire/retailer-visitor-shared/components/Carousel/AnimatedCategoryTile";
import {
  ImageTextLinkTile,
  ImageTextLinkTileStyles,
} from "@faire/retailer-visitor-shared/components/Carousel/ImageTextLinkTile";
import { AspectRatioPicture } from "@faire/retailer-visitor-shared/components/Image/AspectRatioPicture";
import {
  BaseCarouselProps,
  CarouselBase,
  IProps,
} from "@faire/retailer-visitor-shared/components/LayoutElementRenderer/__internal__/Carousel/CarouselBase";
import { Context } from "@faire/retailer-visitor-shared/components/LayoutElementRenderer/__internal__/Context";
import { EAEAEA_PIXEL } from "@faire/retailer-visitor-shared/consts/EAEAEA_PIXEL";
import { ShowroomAnimatedCategoryTile } from "@faire/retailer-visitor-shared/features/Showroom/ShowroomAnimatedCategoryTile/ShowroomAnimatedCategoryTile";
import { VERTICAL_ASPECT_RATIO } from "@faire/retailer-visitor-shared/lib/verticalAspectRatio";
import { optimizeProductImageUrlFillVertical } from "@faire/web/common/images/optimizeImages";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { isNotUndefined } from "@faire/web/common/typescriptUtils";
import { Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { EmbeddedLink } from "@faire/web/ui/EmbeddedLink";
import { useResponsiveValue } from "@faire/web/ui/hooks/useResponsiveValue";
import { TransparencyOverlay } from "@faire/web/ui/TransparencyOverlay";
import { ICategoryTile } from "@faire/web-api--source/indigofair/data/ICategoryTile";
import { ILayoutElement } from "@faire/web-api--source/indigofair/data/ILayoutElement";
import { ISimpleImageTitle } from "@faire/web-api--source/indigofair/data/ISimpleImageTitle";
import { path as categoryShowroom } from "@faire/web-api--source/routes/www/category/Showroom";
import React from "react";
import styled, { css } from "styled-components";

type CategoryTileAndImageTitleTileType = ISimpleImageTitle | ICategoryTile;

const isSimpleImageTile = (
  tile?: CategoryTileAndImageTitleTileType
): tile is ISimpleImageTitle => {
  return "main_text" in (tile ?? {});
};

const isCategoryTile = (
  tile?: CategoryTileAndImageTitleTileType
): tile is ICategoryTile => {
  return "category_path_identifier" in (tile ?? {});
};

const ITEMS_PER_SLIDE_HOMEPAGE = {
  mobileAndAbove: 2,
  tabletAndAbove: 4,
  desktopAndAbove: 6,
};

export const TallCategoryTiles: React.FC<BaseCarouselProps> = (props) => {
  const CarouselBaseComponent = CarouselBase as React.ComponentType<
    IProps<CategoryTileAndImageTitleTileType>
  >;
  const { layoutElement, onItemClick, requestId, onVisible } =
    React.useContext(Context);
  const isSSR = useIsSSR();

  const getItems = () => {
    return layoutElement.layout_element_data
      .map(
        (elementData) =>
          elementData.category_tile ?? elementData.simple_image_title
      )
      .filter(isNotUndefined)
      .filter((tile) =>
        isNotUndefined(isCategoryTile(tile) ? tile.name : tile.main_text)
      )
      .filter(
        (tile) =>
          isNotUndefined(tile.image) ||
          (isNotUndefined(tile.images) && tile.images.length > 0)
      );
  };

  const expectedItemsPerSlideCategoryPage = useResponsiveValue({
    mobileAndAbove: 2,
    tabletAndAbove: 4,
    desktopAndAbove: 6,
    xLargeAndAbove: 7,
  });

  const actualItemsPerSlideCategoryPage = Math.min(
    getItems().length,
    expectedItemsPerSlideCategoryPage
  );

  const imageSizeInfo = () => {
    if (
      layoutElement.display_page !== ILayoutElement.DisplayPage.CATEGORY_PAGE &&
      layoutElement.display_page !== ILayoutElement.DisplayPage.SHOWROOM_PAGE
    ) {
      return {
        height: 250,
        width: 250 * VERTICAL_ASPECT_RATIO,
      };
    }

    if (
      expectedItemsPerSlideCategoryPage - actualItemsPerSlideCategoryPage ===
      1
    ) {
      return { height: 246, width: 246 };
    }

    if (
      expectedItemsPerSlideCategoryPage - actualItemsPerSlideCategoryPage ===
      2
    ) {
      return { height: 219, width: 329 };
    }

    if (
      expectedItemsPerSlideCategoryPage - actualItemsPerSlideCategoryPage ===
      3
    ) {
      return { height: 304, width: 456 };
    }

    if (
      expectedItemsPerSlideCategoryPage - actualItemsPerSlideCategoryPage ===
      4
    ) {
      return { height: 405, width: 608 };
    }

    return {
      height: 256,
      width: 192,
    };
  };

  const renderSimpleImageTitleTile = (
    tile: ISimpleImageTitle,
    index: number
  ) => {
    if (
      layoutElement.display_page !== ILayoutElement.DisplayPage.CATEGORY_PAGE
    ) {
      return null;
    }

    const { image, images = [], cta_link = "", main_text = "" } = tile;

    const handleTitleTileClick = () => {
      onItemClick?.(
        layoutElement.on_click_event_name ?? "",
        requestId,
        tile.event_tracking_token ?? ""
      );
    };

    return (
      <ImageTextLinkTile
        key={index}
        link={cta_link}
        image={image}
        images={images}
        text={main_text}
        imageSizeInfo={imageSizeInfo()}
        handleStyledLinkOnClick={handleTitleTileClick}
        useShowroomAnimatedTile={cta_link.includes(categoryShowroom())}
        dataTestId="rectangular-title-tile"
      />
    );
  };

  const renderCategoryTile = (tile: ICategoryTile, index: number) => {
    const isShowroom = tile.category_path_identifier?.includes(
      SHOWROOM_PATH_IDENTIFIER
    );
    if ((!tile.image && !isShowroom) || !tile.name) {
      return null;
    }
    const useAnimatedTile =
      !!layoutElement.background_color ||
      layoutElement.display_page === ILayoutElement.DisplayPage.CATEGORY_PAGE;

    const handleOnClick = () => {
      onItemClick?.(
        layoutElement.on_click_event_name ?? "",
        requestId,
        tile.category_path_identifier ?? ""
      );
    };

    if (useAnimatedTile) {
      return (
        <StyledAnimatedCategoryTile
          key={index}
          categoryTile={tile}
          imageSizeInfo={imageSizeInfo()}
          $textColor={layoutElement.main_text_color}
          handleStyledLinkOnClick={handleOnClick}
        />
      );
    } else {
      const path = tile.cta_link ?? "";

      return (
        <CategoryTile key={index} linkTo={path} onClick={handleOnClick}>
          {isShowroom ? (
            <ShowroomAnimatedCategoryTile images={tile.images ?? []} />
          ) : (
            <AspectRatioPicture $width="100%" $ratio={VERTICAL_ASPECT_RATIO}>
              <TransparencyOverlay />
              <img
                src={
                  tile.image
                    ? optimizeProductImageUrlFillVertical(
                        tile.image,
                        isSSR,
                        300
                      )
                    : EAEAEA_PIXEL
                }
                alt={tile.name}
                style={{
                  width: "100%",
                  height: "100%",
                }}
                width="100%"
                height="100%"
              />
            </AspectRatioPicture>
          )}
          <Spacer height="1x" />
          <Typography color={layoutElement.main_text_color}>
            {tile.name}
          </Typography>
        </CategoryTile>
      );
    }
  };

  const renderItem = (
    tile: CategoryTileAndImageTitleTileType,
    index: number
  ) => {
    // ISimpleImageTitle tile is only used with this carousel on logged in category pages
    if (isSimpleImageTile(tile)) {
      return renderSimpleImageTitleTile(tile, index);
    }

    return renderCategoryTile(tile, index);
  };

  const onItemsVisible = (items: CategoryTileAndImageTitleTileType[]) => {
    const itemTokens = items.map((item) =>
      isCategoryTile(item)
        ? item.category_path_identifier
        : item.event_tracking_token
    );

    if (layoutElement.on_click_event_name) {
      onVisible?.(
        layoutElement.on_click_event_name,
        requestId,
        itemTokens?.join(" ") ?? ""
      );
    }
  };

  const getItemsPerSlide = () => {
    if (
      layoutElement.display_page === ILayoutElement.DisplayPage.CATEGORY_PAGE ||
      layoutElement.display_page === ILayoutElement.DisplayPage.SHOWROOM_PAGE
    ) {
      return actualItemsPerSlideCategoryPage;
    }

    return props.itemsPerSlide ?? ITEMS_PER_SLIDE_HOMEPAGE;
  };

  const getPeekNextPercent = () => {
    // don't try to peek the next tile if there is only enough content for one slide
    if (
      (layoutElement.display_page ===
        ILayoutElement.DisplayPage.CATEGORY_PAGE ||
        layoutElement.display_page ===
          ILayoutElement.DisplayPage.SHOWROOM_PAGE) &&
      getItems().length <= expectedItemsPerSlideCategoryPage
    ) {
      return undefined;
    }

    return props.peekNextPercent;
  };

  return (
    <CarouselBaseComponent
      buttonsOffsetTop="calc(50% - 20px)"
      renderItem={renderItem}
      items={getItems()}
      onItemsVisible={onItemsVisible}
      {...props}
      itemsPerSlide={getItemsPerSlide()}
      peekNextPercent={getPeekNextPercent()}
    />
  );
};

const CategoryTile = styled(EmbeddedLink)`
  display: flex;
  flex-direction: column;
  text-decoration: none;

  ${responsiveMediaQueries.canHover} {
    &:hover {
      text-decoration: none;
    }
  }
`;

const StyledAnimatedCategoryTile = styled(AnimatedCategoryTile)<{
  $textColor?: string;
}>`
  ${ImageTextLinkTileStyles.Text} {
    color: ${({ $textColor }) => css`
      ${$textColor}
    `};
  }
  ${ImageTextLinkTileStyles.UnderlineText} {
    background-image: ${({ $textColor }) =>
      css`linear-gradient(${$textColor}, ${$textColor})`};
  }
`;
